import type { Node } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import flagShortPoleSvg from '@/assets/icons/flag-short-pole.svg?raw'
import flagShortPoleFilledSvg from '@/assets/icons/flag-short-pole-filled.svg?raw'
import { isWarningCaseStatus, type WarningCaseStatus } from '@/citator/schema/case-status-schema'
import { kInlineTreatmentFlagFrameClasses, kInlineTreatmentFlagGlyphClasses, kCaseTreatmentVisualsByStatus } from '@/citator/utils/case-treatment-visuals'
import { kCaseStatusDefinitions } from '@/citator/utils/case-status-definitions'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'

export const inlineTreatmentFlagKey = new PluginKey<DecorationSet>('inlineTreatmentFlag')

type InlineTreatmentFlagPluginOptions = {
  /** Flags render only while this holds (chat-parity gating: no handler, no flags). */
  isEnabled: () => boolean
  /** Live references lookup — read per rebuild so reference updates are picked up. */
  getReferencesByCitationId: () => Record<string, AgentV2Reference> | undefined
  /** Opens the citation's treatment detail; the host owns click analytics (plain-DOM widget, no hooks). */
  onFlagClick: (citationId: string) => void
  /** Fired once per citation id + status for the plugin's lifetime, when its flag first draws. */
  onFlagImpression: (citationId: string, caseStatus: WarningCaseStatus) => void
}

type FlaggedRange = {
  citationId: string
  caseStatus: WarningCaseStatus
  /** Document position just after the citation's last text node. */
  widgetPosition: number
}

/**
 * Widget decorations rendering the inline treatment flag after each resolved,
 * warning-status caselaw legal-citation mark range. Decorations are view-layer
 * only: they can never serialize into the document, saved markdown, or exports.
 * Numbered reference markers carry no flag, matching the chat rule.
 *
 * The decoration set lives in plugin state: rebuilt on document changes and on
 * the host's references-changed meta (marks don't encode case_status, so a
 * status can change under an unchanged doc), and mapped forward otherwise so
 * selection-only transactions reuse the existing widget DOM.
 */
export function createInlineTreatmentFlagPlugin(options: InlineTreatmentFlagPluginOptions): Plugin<DecorationSet> {
  const seenImpressions = new Set<string>()
  const build = (doc: Node) => buildDecorationSet(doc, options, seenImpressions)

  return new Plugin<DecorationSet>({
    key: inlineTreatmentFlagKey,
    state: {
      init: (_, state) => build(state.doc),
      apply: (transaction, decorationSet) =>
        transaction.docChanged || transaction.getMeta(inlineTreatmentFlagKey)
          ? build(transaction.doc)
          : decorationSet.map(transaction.mapping, transaction.doc),
    },
    props: {
      decorations(state) {
        return inlineTreatmentFlagKey.getState(state)
      },
    },
  })
}

function buildDecorationSet(doc: Node, options: InlineTreatmentFlagPluginOptions, seenImpressions: Set<string>): DecorationSet {
  if (!options.isEnabled()) return DecorationSet.empty
  const referencesByCitationId = options.getReferencesByCitationId()
  if (!referencesByCitationId) return DecorationSet.empty
  // Most documents cite no bad law — skip the doc walk entirely when no reference
  // could produce a flag.
  const hasWarningReference = Object.values(referencesByCitationId).some(
    (reference) => reference.source_type === 'caselaw' && isWarningCaseStatus(reference.case_status)
  )
  if (!hasWarningReference) return DecorationSet.empty

  const decorations = collectFlaggedRanges(doc, referencesByCitationId).map((range, occurrenceIndex) =>
    Decoration.widget(range.widgetPosition, () => buildFlagElement(range, options, seenImpressions), {
      // Occurrence-indexed (not position-keyed) so edits elsewhere in the doc keep
      // widget identity and ProseMirror reuses the existing flag DOM.
      key: `treatment-flag:${range.citationId}:${range.caseStatus}:${occurrenceIndex}`,
      side: 1,
      // Editing around the flag must never type "into" it or its citation's marks.
      marks: [],
      // The flag is UI, not content: keep its events (keydown especially — a focused
      // flag's Enter must not reach the editor keymap) and focus out of editor handling.
      stopEvent: () => true,
      ignoreSelection: true,
    })
  )
  return DecorationSet.create(doc, decorations)
}

function collectFlaggedRanges(doc: Node, referencesByCitationId: Record<string, AgentV2Reference>): FlaggedRange[] {
  const ranges: FlaggedRange[] = []
  let openRange: FlaggedRange | null = null

  doc.descendants((node, position) => {
    const mark = node.isText ? node.marks.find((candidate) => candidate.type.name === 'legalCitation') : undefined
    if (!mark || !mark.attrs.isResolved || !mark.attrs.canOpen) {
      if (openRange) ranges.push(openRange)
      openRange = null
      return true
    }

    const citationId = mark.attrs.citationId as string
    const reference = referencesByCitationId[citationId]
    const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null
    if (!isWarningCaseStatus(caseStatus)) {
      if (openRange) ranges.push(openRange)
      openRange = null
      return true
    }

    const endPosition = position + node.nodeSize
    if (openRange && openRange.citationId === citationId) {
      // Adjacent text nodes of one citation (e.g. an italicized case name) share one flag.
      openRange.widgetPosition = endPosition
      return true
    }
    if (openRange) ranges.push(openRange)
    openRange = { citationId, caseStatus, widgetPosition: endPosition }
    return true
  })
  if (openRange) ranges.push(openRange)

  return ranges
}

function buildFlagElement(range: FlaggedRange, options: InlineTreatmentFlagPluginOptions, seenImpressions: Set<string>): HTMLElement {
  const { citationId, caseStatus } = range
  const { colorClass, filled } = kCaseTreatmentVisualsByStatus[caseStatus]
  const heading = kCaseStatusDefinitions[caseStatus].heading

  const button = document.createElement('button')
  button.type = 'button'
  button.contentEditable = 'false'
  // Gap and baseline em-scale the chat spec's 4px / -3px (at 14px prose) to the editor's type.
  button.className = `inline-treatment-flag ${kInlineTreatmentFlagFrameClasses} ml-[0.286em] align-[-0.214em] ${colorClass}`
  button.setAttribute('aria-label', heading)
  button.title = heading
  // Trusted static asset content — the two flag SVGs shipped in src/assets/icons.
  button.innerHTML = filled ? flagShortPoleFilledSvg : flagShortPoleSvg
  const svgElement = button.querySelector('svg')
  svgElement?.setAttribute('aria-hidden', 'true')
  svgElement?.setAttribute('class', kInlineTreatmentFlagGlyphClasses)

  button.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    options.onFlagClick(citationId)
  })
  // stopEvent keeps keydown from the editor, so the button's native Enter/Space
  // activation (which fires as a click) never happens — activate it ourselves.
  button.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.stopPropagation()
    options.onFlagClick(citationId)
  })

  // Impression = flag drawn; once per citation + status for this editor instance so
  // rebuilds cannot inflate the metric. See AgentV2Markdown's impression effect for
  // the chat twin — the two must stay authority-denominated the same way.
  const impressionKey = `${citationId}:${caseStatus}`
  if (!seenImpressions.has(impressionKey)) {
    seenImpressions.add(impressionKey)
    options.onFlagImpression(citationId, caseStatus)
  }

  return button
}
